import { useEffect } from 'react';
import { useParams } from 'react-router';
import { BarChart3, Info } from 'lucide-react';
import { cn } from 'helpers/cn';
import { useQueryState } from 'hooks/useQueryState';
import { useSurvey } from '@/state/surveys/useSurvey';
import { useTourScreen } from '@/tours';
import { SurveyResults } from '../Results';
import { Header } from './Header';
import { Overview } from './Overview';

export type Screens = 'overview' | 'results' | '';

const TABS: { value: Exclude<Screens, ''>; label: string; icon: typeof Info }[] = [
  { value: 'overview', label: 'Overview', icon: Info },
  { value: 'results', label: 'Results', icon: BarChart3 },
];

/**
 * The landing screen for a single survey, mirroring the experiment details hub.
 *
 * Clicking a survey used to drop straight into the multi-step builder. Besides
 * being inconsistent with experiments, it invited question edits on a Running
 * survey, which would orphan the responses already collected against the old
 * question ids — the API rejects it, so the shortcut only ever led to a 409.
 */
export const SurveyDetailsScreen = () => {
  const { id } = useParams();
  const [screen, setScreen] = useQueryState<Screens>('screen', '');
  const { survey } = useSurvey(Number(id));
  const isDraft = survey?.status === 'Draft';
  // Each tab has its own tour, and /surveys/:id/results lands here, so say which tab is open.
  useTourScreen(screen || null);

  useEffect(() => {
    // A draft has nothing to show under Results, so it opens on the summary.
    if (survey && !screen) setScreen(isDraft ? 'overview' : 'results');
  }, [survey, screen, isDraft, setScreen]);

  if (!survey || !screen) return null;

  return (
    <div className="flex flex-col gap-[14px]">
      <Header survey={survey} />

      <div className="flex flex-wrap items-center gap-1.5">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => setScreen(tab.value)}
              className={cn(
                'micro-label rounded-pill border-line inline-flex items-center gap-1.5 border px-3 py-1.5',
                'transition-colors duration-150',
                screen === tab.value
                  ? 'bg-sel-bg border-sel-line text-foreground'
                  : 'text-dim hover:bg-hover hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {screen === 'overview' && <Overview survey={survey} />}

      {/* A draft has no summary row to fetch, so asking for one would surface an
          error where there is simply nothing yet. */}
      {screen === 'results' && isDraft && (
        <section
          className="bg-panel border-line rounded-panel flex flex-col items-center border px-6 py-12 text-center"
          data-first-run
        >
          <BarChart3 className="text-dim mb-4 h-10 w-10" strokeWidth={1.5} />
          <h3 className="text-foreground m-0 text-body font-medium">
            Results will be available once the survey starts
          </h3>
          <p className="supporting mt-1">Start the survey to begin collecting responses.</p>
        </section>
      )}

      {screen === 'results' && !isDraft && <SurveyResults embedded />}
    </div>
  );
};
